import React, { FC, useEffect, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import { useDispatch, useSelector } from 'react-redux'
import { endOfMonth, isBefore, startOfMonth } from 'date-fns'
import { Spacer } from 'components/Utility'
import { Description, MainHeadline } from 'components/Typography'
import { AlertCard, ScheduleCollectiveConfirmCardList } from 'components/Card'
import { NavButton, OrangeButton } from 'components/Button'
import { State } from 'index'
import {
  getConfirmCollectiveStocksBreads,
  getConfirmCollectiveStocksDates,
  getConfirmCollectiveStocksDeletedMonth,
} from 'reducks/confirm/selectors'
import { fetchFutureOrders, initOrders, unsubscribeOrders } from 'reducks/checkout/operations'
import { fetchSchedules } from 'reducks/schedule/operations'
import { fetchStoreCalendars } from 'reducks/storeCalendar/operations'
import { fetchStocksWithinPeriod, saveCollectiveStocks } from 'reducks/stock/operations'
import { getStoreRef } from 'reducks/auth/selectors'
import { getSchedules } from 'reducks/schedule/selectors'
import { getStocks } from 'reducks/stock/selectors'
import { ConfirmStock } from 'types/stock'
import { OrderType } from 'types/checkout'
import { getOrders } from 'reducks/checkout/selectors'
import { useCheckoutItemsFromOrders } from 'hooks/checkout'
import { useConfirmStocks } from 'hooks/stock'
import { getSpecialHolidays } from 'reducks/calendar/selectors'
import { getStoreCalendars } from 'reducks/storeCalendar/selectors'
import { useQueryStoreName } from 'hooks/store'
import { CheckoutItemType } from 'types/checkoutItem'
const type = 'preorder'

const ScheduleCollectiveEditConfirmTemplate: FC = () => {
  const dispatch = useDispatch()
  const breads = useSelector((state: State) => getConfirmCollectiveStocksBreads(state))
  const orders: OrderType[] = useSelector((state: State) => getOrders(state))
  const selectedDates: Date[] = useSelector((state: State) => getConfirmCollectiveStocksDates(state))
  const deletedMonth: Date = useSelector((state: State) => getConfirmCollectiveStocksDeletedMonth(state))
  const schedules = useSelector((state: State) => getSchedules(state))
  const specialHolidays = useSelector((state: State) => getSpecialHolidays(state))
  const storeCalendars = useSelector((state: State) => getStoreCalendars(state))
  const stocks = useSelector((state: State) => getStocks(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))

  const [confirmStocks, setConfirmStocks] = useState<ConfirmStock[]>([])

  const checkoutItems: CheckoutItemType[] = useCheckoutItemsFromOrders(orders)
  const storeName = useQueryStoreName(storeRef)

  // 事前予約商品の注文一覧
  useEffect(() => {
    if (!storeRef) return
    dispatch(initOrders())
    dispatch(fetchFutureOrders(storeRef, ['succeeded', 'requires_capture'], [type]))
    return () => {
      dispatch(unsubscribeOrders())
    }
  }, [storeRef])

  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchSchedules(storeRef, [type]))
  }, [storeRef, type])

  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchStoreCalendars(storeRef, startOfMonth(new Date())))
  }, [storeRef])

  // 選択された日付範囲の月初〜月末の在庫データを取得
  useEffect(() => {
    if (!storeRef) return
    const today = new Date()

    if (typeof deletedMonth === 'undefined' && selectedDates.length > 0) {
      const dates = selectedDates.sort((a, b) => (a < b ? -1 : 1))
      const startDate = dates[0]
      const endDate = dates[dates.length - 1]
      dispatch(
        fetchStocksWithinPeriod({
          storeRef,
          type,
          // 月初が今日より前の日付なら、startを今日にする
          startOfMonth: isBefore(today, startOfMonth(startDate)) ? startOfMonth(startDate) : today,
          endOfMonth: endOfMonth(endDate),
        })
      )
    } else {
      dispatch(
        fetchStocksWithinPeriod({
          storeRef,
          type,
          // 月初が今日より前の日付なら、startを今日にする
          startOfMonth: isBefore(today, startOfMonth(deletedMonth)) ? startOfMonth(deletedMonth) : today,
          endOfMonth: endOfMonth(deletedMonth),
        })
      )
    }
  }, [storeRef, selectedDates, deletedMonth, type])

  useConfirmStocks(
    breads,
    schedules,
    selectedDates,
    setConfirmStocks,
    specialHolidays,
    stocks,
    storeCalendars,
    storeName,
    storeRef
  )

  const showingConfirmedStocks = confirmStocks.filter(
    (confirmStock) => confirmStock.addedStocks.length > 0 || confirmStock.deletedStocks.length > 0
  )

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <MainHeadline headline={'修正内容の確認'} />
      <Spacer size={20} />
      {showingConfirmedStocks.length > 0 ? (
        <>
          <AlertCard
            description={
              '登録される日は青字、削除される日は赤字で表示しています。必ずご確認いただき保存を行ってください。'
            }
            headline={'受渡可能日を確認してください。'}
            title={'注意'}
          />
          <Spacer size={16} />
          <OrangeButton
            label={'保存する'}
            onClick={() => dispatch(saveCollectiveStocks(breads, confirmStocks, checkoutItems, type))}
            size={'medium'}
            type={'main'}
          />
          <Spacer size={48} />
          <ScheduleCollectiveConfirmCardList
            // 追加・削除される商品のみ表示
            confirmStocks={showingConfirmedStocks}
            schedules={schedules}
          />
        </>
      ) : (
        <Description description={'すでに登録されている受渡可能日に変更はありません。'} />
      )}
      <Spacer size={48} />
      <OrangeButton
        label={'保存する'}
        onClick={() => dispatch(saveCollectiveStocks(breads, confirmStocks, checkoutItems, type))}
        size={'medium'}
        type={'main'}
      />
      <Spacer size={20} />
      <NavButton
        label={'事前予約受付設定に戻る'}
        iconPath={'/icon/grey/arrow_prev.svg'}
        url={'/schedule/collective_setting'}
      />
      <Spacer size={72} />
    </div>
  )
}

export default ScheduleCollectiveEditConfirmTemplate
